import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Minus, Square, Maximize2 } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

export default function Window({ 
  id,
  title, 
  icon: Icon, 
  children, 
  zIndex,
  onClose,
  onMinimize,
  onFocus,
  position = { x: 150, y: 60 },
  onPositionChange,
  width = 600,
  height = 400,
  resizable = true
}) {
  const [isMaximized, setIsMaximized] = useState(false);
  const [size, setSize] = useState({ width, height });
  const [isResizing, setIsResizing] = useState(false);
  const windowRef = useRef(null);

  const handleClose = (e) => {
    e.stopPropagation();
    playSound(SOUNDS.windowClose);
    onClose();
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    playSound(SOUNDS.click);
    onMinimize();
  };

  const handleMaximize = (e) => {
    if (e) e.stopPropagation();
    playSound(SOUNDS.click);
    setIsMaximized(!isMaximized);
  };

  // Resize handlers
  const handleResizeStart = (e, direction) => {
    if (!resizable || isMaximized) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;
    const startPosX = position.x;
    const startPosY = position.y;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPosX;
      let newY = startPosY;

      if (direction.includes('e')) {
        newWidth = Math.max(300, startWidth + deltaX);
      }
      if (direction.includes('w')) {
        const widthDelta = Math.min(deltaX, startWidth - 300);
        newWidth = startWidth - widthDelta;
        newX = startPosX + widthDelta;
      }
      if (direction.includes('s')) {
        newHeight = Math.max(200, startHeight + deltaY);
      }
      if (direction.includes('n')) {
        const heightDelta = Math.min(deltaY, startHeight - 200);
        newHeight = startHeight - heightDelta;
        newY = startPosY + heightDelta;
      }

      setSize({ width: newWidth, height: newHeight });
      if (direction.includes('w') || direction.includes('n')) {
        onPositionChange?.({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const windowStyle = isMaximized
    ? { left: 0, top: 0, width: '100%', height: 'calc(100vh - 36px)', zIndex }
    : { left: position.x, top: position.y, width: size.width, height: size.height, zIndex };

  return (
    <motion.div
      ref={windowRef}
      className={`win-window floating-window ${isMaximized ? 'maximized' : ''}`}
      style={windowStyle}
      initial={{ opacity: 0, scale: 0.92, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      transition={{ 
        duration: 0.2, 
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      onClick={onFocus}
      drag={!isMaximized && !isResizing}
      dragMomentum={false}
      dragElastic={0.05}
      dragConstraints={{ left: -500, right: 1400, top: 0, bottom: 700 }}
      onDragEnd={(event, info) => {
        if (!isMaximized && !isResizing) {
          const newX = Math.max(0, position.x + info.offset.x);
          const newY = Math.max(0, position.y + info.offset.y);
          onPositionChange?.({ x: newX, y: newY });
        }
      }}
    >
      <div 
        className="win-window-header" 
        style={{ cursor: isMaximized ? 'default' : 'grab' }}
        onDoubleClick={handleMaximize}
      >
        <div className="win-window-title">
          {Icon && <Icon size={14} />}
          {title}
        </div>
        <div className="win-window-controls">
          <button className="win-ctrl-btn" onClick={handleMinimize} title="Minimize">
            <Minus size={10} />
          </button>
          <button className="win-ctrl-btn" onClick={handleMaximize} title={isMaximized ? "Restore" : "Maximize"}>
            {isMaximized ? <Square size={8} /> : <Maximize2 size={10} />}
          </button>
          <button className="win-ctrl-btn win-close" onClick={handleClose} title="Close">
            <X size={10} />
          </button>
        </div>
      </div>
      
      <div className="win-window-body" style={{ height: 'calc(100% - 28px)' }}>
        {children}
      </div>

      {/* Resize handles */}
      {resizable && !isMaximized && (
        <>
          <div className="resize-handle resize-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
          <div className="resize-handle resize-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
          <div className="resize-handle resize-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
          <div className="resize-handle resize-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
          <div className="resize-handle resize-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
          <div className="resize-handle resize-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
          <div className="resize-handle resize-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
          <div className="resize-handle resize-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
        </>
      )}
    </motion.div>
  );
}
